<script setup>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import MarketingCtaBtn from '~/components/marketing/MarketingCtaBtn.vue';
import TvDemoStage from '~/components/marketing/TvDemoStage.vue';
import {TV_PANEL, TV_PANEL_ICON, TV_PANEL_KIND_NAME} from '~/utils/tvDisplay';

// Clubhouse TV: the screen on the clubhouse wall. The page runs the real
// screen on demo data, because the feature is the screen.
definePageMeta({
  name: 'FeaturesClubhouseTv',
  path: '/features/clubhouse-tv',
});

const {setMeta} = useHeadService();
const {accountCtaLabel} = useMarketingCta();

setMeta(
    'Clubhouse TV: a live screen for your club | Tap Leagues',
    'Put a live screen in the clubhouse: live scores, latest results, league tables, ladders, draws, who is playing next and a QR code to join. Any TV with a browser. Included with Tap Leagues.',
);

const PANEL_BLURB = {
  [TV_PANEL.Splash]: 'The club, how many competitions are running, results this week and the next match.',
  [TV_PANEL.LiveNow]: 'Matches being scored right now, game by game and point by point.',
  [TV_PANEL.ComingUp]: 'The next scheduled matches, with court and time.',
  [TV_PANEL.RecentResults]: 'The latest results as they are entered, newest first.',
  [TV_PANEL.LeagueStandings]: 'A division table, paged when it is long.',
  [TV_PANEL.Ladder]: 'Positions, moves since last time, open challenges and frozen players.',
  [TV_PANEL.TournamentDraw]: 'Group tables while the groups run, then the bracket.',
  [TV_PANEL.AmericanoStandings]: 'Personal points for a group, with sets and games.',
  [TV_PANEL.Meet]: 'The next session: who is going, spaces left, the waitlist.',
  [TV_PANEL.Challenges]: 'Open and accepted ladder challenges, with the predicted chance of success.',
  [TV_PANEL.Leaderboard]: 'The club\'s top players this season.',
  [TV_PANEL.MatchReports]: 'AI match reports for recent results, where the club has them on.',
  [TV_PANEL.LeagueGrid]: 'A division\'s results grid, every match in one view.',
};
const panels = Object.values(TV_PANEL).map(kind => ({
  kind,
  name: TV_PANEL_KIND_NAME[kind],
  icon: TV_PANEL_ICON[kind],
  blurb: PANEL_BLURB[kind],
}));

const FAQS = [
  {q: 'What do I need?', a: 'A TV or monitor with a browser, or anything that can show one: a smart TV, a Chromecast, a Fire Stick, a Raspberry Pi, an old laptop. Open the screen\'s link in full screen and leave it.'},
  {q: 'Does it need a login?', a: 'No. Each screen has its own private link. Nobody signs in on the clubhouse wall, and the link can be regenerated if it gets out.'},
  {q: 'How live is it?', a: 'Results appear seconds after they are entered, and a match being scored live updates point by point. Nothing on the screen needs refreshing.'},
  {q: 'Can we have more than one screen?', a: 'Yes. Each screen is set up separately, with its own panels, theme and colour, so the bar can show something different from the courts.'},
  {q: 'Does it work on a portrait screen?', a: 'Yes. The panels reflow for a tall screen rather than rotating.'},
  {q: 'What does it cost?', a: 'Nothing. Clubhouse TV is part of Tap Leagues.'},
];

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {'@type': 'Answer', text: faq.a},
      })),
    }),
  }],
});
</script>

<template>
  <base-page>
    <!-- ===== HERO ===== -->
    <section class="ctv-hero">
      <div class="ctv-hero-glow" aria-hidden="true"/>
      <v-container class="ctv-wrap ctv-hero-inner">
        <NuxtLink to="/features" class="ctv-back"><v-icon size="16" icon="mdi-arrow-left"/> All features</NuxtLink>
        <span class="ctv-eyebrow">Clubhouse TV</span>
        <h1 class="ctv-title">A live screen for the clubhouse</h1>
        <p class="ctv-lead">
          Live scores, the latest results, league tables, ladders, draws, who is on next and a code to join,
          rotating on any TV with a browser. It updates itself the moment a result goes in.
        </p>
        <div class="ctv-hero-cta">
          <marketing-cta-btn color="primary" size="large">{{ accountCtaLabel }}</marketing-cta-btn>
        </div>
        <p class="ctv-note">Free with Tap Leagues.* <commission-note/></p>
      </v-container>
    </section>

    <v-container class="ctv-wrap">
      <!-- ===== THE DEMO ===== -->
      <section class="ctv-demo" aria-labelledby="ctv-demo-title">
        <h2 id="ctv-demo-title" class="ctv-h2">This is the screen, running</h2>
        <p class="ctv-sub">
          A made-up club, the real thing otherwise. Change the look, and press Enter a result to see what the
          bar sees when someone enters a score.
        </p>
        <tv-demo-stage class="ctv-stage"/>
      </section>

      <!-- ===== PANELS ===== -->
      <section class="ctv-section" aria-labelledby="ctv-panels-title">
        <h2 id="ctv-panels-title" class="ctv-h2">Thirteen panels</h2>
        <p class="ctv-sub">Show the ones you want, pin the ones that matter, set how long each stays up.</p>
        <div class="ctv-panels tl-card" role="list">
          <div v-for="panel in panels" :key="panel.kind" class="ctv-panel" role="listitem">
            <span class="ctv-panel-icon"><v-icon size="18" :icon="panel.icon"/></span>
            <h3 class="ctv-panel-name">{{ panel.name }}</h3>
            <p class="ctv-panel-blurb">{{ panel.blurb }}</p>
          </div>
        </div>
      </section>

      <!-- ===== HOW IT WORKS ===== -->
      <section class="ctv-section" aria-labelledby="ctv-how-title">
        <h2 id="ctv-how-title" class="ctv-h2">Setting it up</h2>
        <ol class="ctv-steps">
          <li class="tl-card">
            <span class="ctv-step-n">1</span>
            <h3>Add a screen</h3>
            <p>Under Clubhouse TV in the club's admin area. Name it, pick light or dark, broadcast or clubhouse, and your colour.</p>
          </li>
          <li class="tl-card">
            <span class="ctv-step-n">2</span>
            <h3>Choose the panels</h3>
            <p>Every competition at the club is offered. Show, hide, pin and order them, and switch the ticker, live scores and celebrations on or off.</p>
          </li>
          <li class="tl-card">
            <span class="ctv-step-n">3</span>
            <h3>Open the link on the TV</h3>
            <p>Any browser, full screen. No login, nothing to install. It stays current on its own and comes back by itself after a power cut.</p>
          </li>
        </ol>
      </section>

      <!-- ===== DETAILS ===== -->
      <section class="ctv-section" aria-labelledby="ctv-details-title">
        <h2 id="ctv-details-title" class="ctv-h2">What else it does</h2>
        <ul class="ctv-details">
          <li><v-icon size="16" icon="mdi-check-circle" class="ctv-check"/>Live scores point by point, with a LIVE bug in the top bar while anything is being scored.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="ctv-check"/>A celebration when a result lands, then it joins the results crawl along the bottom.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="ctv-check"/>A QR code to join the club, and one on any competition that is open to join.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="ctv-check"/>Short panels share the screen side by side. Long tables page through.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="ctv-check"/>Landscape or portrait. Text stays the same size on a 32 inch screen and a 75 inch one.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="ctv-check"/>As many screens as you like, each with its own panels and look.</li>
        </ul>
      </section>

      <!-- ===== FAQ ===== -->
      <section class="ctv-section" aria-labelledby="ctv-faq-title">
        <h2 id="ctv-faq-title" class="ctv-h2">Questions</h2>
        <div class="ctv-faqs">
          <div v-for="faq in FAQS" :key="faq.q" class="ctv-faq tl-card">
            <h3>{{ faq.q }}</h3>
            <p>{{ faq.a }}</p>
          </div>
        </div>
      </section>

      <!-- ===== CTA ===== -->
      <section class="ctv-final">
        <div class="ctv-final-streaks" aria-hidden="true"/>
        <div class="ctv-final-inner">
          <h2 class="ctv-final-title">Put it on the wall this week</h2>
          <p class="ctv-final-sub">Set up the club, add a screen, open the link on the TV.</p>
          <div class="ctv-final-cta">
            <marketing-cta-btn color="white" size="large" class="text-primary">{{ accountCtaLabel }}</marketing-cta-btn>
            <v-btn variant="outlined" color="white" size="large" to="/features">All features</v-btn>
          </div>
        </div>
      </section>
    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
.ctv-wrap {
  max-width: 1100px;
}

.ctv-hero {
  position: relative;
  overflow: hidden;
  background:
      radial-gradient(circle at 14% 0%, rgba(211, 41, 70, 0.08), transparent 46%),
      linear-gradient(180deg, rgb(var(--v-theme-surface)) 0%, rgb(var(--v-theme-light)) 100%);
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.ctv-hero-inner {
  padding-top: 32px;
  padding-bottom: 36px;

  @media (min-width: 960px) {
    padding-top: 44px;
    padding-bottom: 48px;
  }
}

.ctv-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
  text-decoration: none;
  margin-bottom: 18px;
}

.ctv-eyebrow {
  display: block;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 12px;
}

.ctv-title {
  font-size: clamp(30px, 5vw, 46px);
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.05;
  max-width: 760px;
}

.ctv-lead {
  font-size: 16px;
  line-height: 1.7;
  color: rgba(var(--v-theme-charcoal), 0.72);
  max-width: 720px;
  margin-top: 14px;
}

.ctv-hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}

.ctv-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 10px;
}

.ctv-h2 {
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
}

.ctv-sub {
  font-size: 15px;
  line-height: 1.65;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 8px;
  max-width: 640px;
}

.ctv-demo {
  margin-top: 40px;

  @media (min-width: 960px) {
    margin-top: 56px;
  }
}

.ctv-stage {
  margin-top: 20px;
}

.ctv-section {
  margin-top: 48px;

  @media (min-width: 960px) {
    margin-top: 64px;
  }
}

/* Panels: a compact list */
.ctv-panels {
  margin-top: 18px;
  overflow: hidden;
}

.ctv-panel {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 2px 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  @media (min-width: 760px) {
    grid-template-columns: 32px minmax(200px, 30%) minmax(0, 1fr);
    gap: 16px;
    align-items: center;
    padding: 11px 16px;
  }
}

.ctv-panel-icon {
  grid-row: 1 / span 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));

  @media (min-width: 760px) {
    grid-row: auto;
  }
}

.ctv-panel-name {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  margin: 0;
}

.ctv-panel-blurb {
  font-size: 13px;
  line-height: 1.5;
  color: rgb(var(--v-theme-charcoal));
  margin: 0;
}

/* Steps */
.ctv-steps {
  list-style: none;
  padding: 0;
  margin: 18px 0 0;
  display: grid;
  gap: 14px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(3, 1fr);
  }

  li {
    padding: 18px;
  }

  h3 {
    font-size: 15px;
    font-weight: 700;
    margin-top: 10px;
  }

  p {
    font-size: 13px;
    line-height: 1.6;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.ctv-step-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 800;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
}

.ctv-details {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: grid;
  gap: 10px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px 24px;
  }

  li {
    display: flex;
    gap: 9px;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.55;
  }
}

.ctv-check {
  color: rgb(var(--v-theme-success));
  margin-top: 2px;
  flex: none;
}

.ctv-faqs {
  display: grid;
  gap: 14px;
  margin-top: 18px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.ctv-faq {
  padding: 18px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 13.5px;
    line-height: 1.65;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.ctv-final {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin-top: 64px;
  margin-bottom: 24px;
  padding: 56px 24px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #E0314F 0%, #D32946 44%, #A31C35 100%);
  box-shadow: 0 30px 60px -30px rgba(163, 28, 53, 0.75);

  @media (min-width: 960px) {
    margin-top: 88px;
    padding: 72px 40px;
  }
}

.ctv-final-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.05) 38px 40px);
}

.ctv-final-inner {
  position: relative;
  z-index: 1;
}

.ctv-final-title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.8px;

  @media (min-width: 960px) {
    font-size: 36px;
  }
}

.ctv-final-sub {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 10px;
}

.ctv-final-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 26px;
}
</style>
